import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Button } from '@sunfish/ui';
import { useForm } from 'react-hook-form';
import { useSearchParams, Link } from 'react-router';
import { Form } from '@sunfish/ui';
import { AuthLayout } from '~/components/auth';
import { InputBlock } from '@sunfish/forms';
import { Alert } from '@sunfish/ui';
import { adminAuthClient } from '~/lib/auth/admin-client';
import type { Provider } from '~/lib/auth/admin-config.server';
import { z } from 'zod';
import googleIcon from '~assets/img/providers/google-icon.svg';

const providers: { name: string; value: Provider; icon: string }[] = [
	{ name: 'Google', value: 'google', icon: googleIcon }
];
const emailPasswordEnabled = true;

const formSchema = z.object({
	email: z.string().email('Invalid email address').min(1, 'Email is required'),
	password: z.string()
});

// Belt-and-suspenders open-redirect guard. tenant-layout already filters next
// before adding it to the URL, but a hand-crafted /login?next=... could land
// here from anywhere, so we re-validate before handing the value to Better Auth.
function safeCallback(next: string | null): string {
	if (!next) return '/events';
	if (!next.startsWith('/') || next.startsWith('//')) return '/events';
	if (next === '/login' || next.startsWith('/login?')) return '/events';
	return next;
}

function friendlySignInError(raw?: string): string {
	const msg = (raw ?? '').toLowerCase();
	if (
		msg.includes('invalid') ||
		msg.includes('password') ||
		msg.includes('credential') ||
		msg.includes('not found') ||
		msg.includes('unauthorized')
	) {
		return "That email or password doesn't match. Try again, or reset your password.";
	}
	return 'Something went wrong signing you in. Please try again.';
}

export default function LoginPage() {
	const [loading, setLoading] = useState(false);
	const [searchParams] = useSearchParams();
	const isUnauth = searchParams.get('unauth') !== null;
	const callbackURL = safeCallback(searchParams.get('next'));

	const form = useForm<z.output<typeof formSchema>>({
		mode: 'onBlur',
		resolver: zodResolver(formSchema)
	});

	const handleOAuthSignIn = async (provider: Provider) => {
		setLoading(true);
		try {
			const response = await adminAuthClient.signIn.social({
				provider,
				callbackURL
			});
			if (response.error) {
				throw new Error(response.error.message?.length ? response.error.message : 'An unexpected error occurred');
			}
		} catch {
			form.setError('root', {
				type: 'manual',
				message: 'Couldn\'t sign in with that provider. Please try again.'
			});
		} finally {
			setLoading(false);
		}
	};

	const onSubmit = async (data: z.output<typeof formSchema>) => {
		setLoading(true);

		try {
			const response = await adminAuthClient.signIn.email({
				email: data.email,
				password: data.password,
				callbackURL
			});
			if (response.error) {
				throw new Error(response.error.message);
			}
		} catch (error) {
			form.setError('root', {
				type: 'manual',
				message: friendlySignInError(error instanceof Error ? error.message : undefined)
			});
		}

		setLoading(false);
	};

	return (
		<AuthLayout>
			<div className="mb-8">
				<h1 className="text-headline text-foreground">Welcome back</h1>
				<p className="mt-2 text-body text-muted-foreground">Sign in to manage your events.</p>
			</div>
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
					{isUnauth && (
						<Alert variant="destructive">
							<span className="text-sm">You are not authorized to access this page.</span>
						</Alert>
					)}
					{form.formState.errors?.root && (
						<Alert variant="destructive">
							<span className="text-sm text-destructive">{form.formState.errors.root.message}</span>
						</Alert>
					)}

					{emailPasswordEnabled && (
						<>
							<InputBlock
								name="email"
								label="Email"
								type="email"
								control={form.control}
								required
								disabled={loading}
								fieldProps={{ autoComplete: 'username' }}
							/>
							<div className="space-y-1.5">
								<InputBlock
									name="password"
									label="Password"
									type="password"
									control={form.control}
									required
									disabled={loading}
									fieldProps={{ autoComplete: 'current-password' }}
								/>
								<Link
									to="/forgot-password"
									className="inline-block text-label font-medium text-foreground underline decoration-brand decoration-2 underline-offset-2 hover:decoration-brand-600"
								>
									Forgot your password?
								</Link>
							</div>
						</>
					)}

					<Button
						disabled={loading}
						type="submit"
						variant="brand"
						size="lg"
						className="w-full"
					>
						{loading ? 'Signing in...' : 'Sign in'}
					</Button>

					{providers?.length > 0 && (
						<>
							<div className="flex items-center gap-3">
								<div className="h-px w-full bg-border" />
								<span className="shrink-0 text-label text-muted-foreground">or continue with</span>
								<div className="h-px w-full bg-border" />
							</div>
							<div className="flex w-full flex-col gap-3">
								{providers.map((provider) => (
									<Button
										type="button"
										key={provider.value}
										variant="outline"
										size="lg"
										onClick={() => {
											handleOAuthSignIn(provider.value);
										}}
										disabled={loading}
									>
										<div className="mr-2 h-5 w-5">
											<img src={provider.icon} alt={provider.name} />
										</div>
										Sign in with {provider.name}
									</Button>
								))}
							</div>
						</>
					)}
				</form>
			</Form>
		</AuthLayout>
	);
}
